import type { GroupWithAll } from "../types.js";
import type { InjectedData } from "../layout/types.js";
import {
  athleteRecordFromValues,
  groupRecordFromValues,
} from "../custom-discipline-behavior/records.js";
import {
  hydrateCustomDisciplineGroupSchema,
  customDisciplineRuntimeMetadata,
} from "./runtime-data.js";
import { withCustomDisciplineCategoryReferences } from "./category-references.js";

type CustomDisciplineFieldLike = {
  id: string;
  key: string;
  scope: string;
  type: string;
  options?: ReadonlyArray<{ id: string; value: string }>;
  referenceSource?: string | null;
  referenceIdPath?: string | null;
  referenceLabelPath?: string | null;
};

export type CustomDisciplineGroupTypeLike = {
  id: string;
  name?: string | null;
  code?: string | null;
  disciplineId?: string | null;
  discipline?: {
    id?: string | null;
    categoryDisciplineId?: number | null;
    name?: string | null;
    code?: string | null;
  } | null;
  fields?: CustomDisciplineFieldLike[];
};

type CustomDisciplineGroupLike = {
  id: string;
  name?: string | null;
  groupTypeId: string;
  values?: unknown[];
  athletes?: Array<{
    id: string;
    groupId?: string | null;
    order?: number | null;
    values?: unknown[];
  }>;
  groupType?: CustomDisciplineGroupTypeLike | null;
};

export type GroupWithCustomDisciplineRuntime = GroupWithAll & {
  customDisciplineGroupId?: string | null;
  customDisciplineGroup?: CustomDisciplineGroupLike | null;
};

export function isCustomDisciplineRuntimeGroup(
  group: unknown,
): group is GroupWithCustomDisciplineRuntime {
  const value = group as GroupWithCustomDisciplineRuntime | null | undefined;
  return Boolean(
    value?.customDisciplineGroup || value?.customDisciplineGroupId,
  );
}

function getCustomDisciplineGroupType(
  group: GroupWithCustomDisciplineRuntime,
): CustomDisciplineGroupTypeLike | null {
  return group.customDisciplineGroup?.groupType ?? null;
}

function getScopeFields(
  groupType: CustomDisciplineGroupTypeLike | null,
  scope: "ATHLETE" | "GROUP",
): CustomDisciplineFieldLike[] {
  return [...(groupType?.fields ?? [])]
    .filter((field) => field.scope === scope)
    .sort((left, right) => {
      const leftOrder = Number((left as { order?: unknown }).order ?? 0);
      const rightOrder = Number((right as { order?: unknown }).order ?? 0);
      return leftOrder - rightOrder;
    });
}

function getCustomDisciplineGroupTypeId(
  group: GroupWithCustomDisciplineRuntime,
): string | null {
  const customGroup = group.customDisciplineGroup;
  return (
    customGroup?.groupTypeId ?? getCustomDisciplineGroupType(group)?.id ?? null
  );
}

function buildCustomGroupRecord(
  group: GroupWithCustomDisciplineRuntime,
  referenceSources: Record<string, readonly unknown[]>,
) {
  const customGroup = group.customDisciplineGroup;
  const groupType = getCustomDisciplineGroupType(group);
  const record = groupRecordFromValues(
    getScopeFields(groupType, "GROUP"),
    (customGroup?.values ?? []) as never,
    {
      id: group.id,
      groupId: group.id,
      customGroupId: customGroup?.id ?? group.customDisciplineGroupId ?? null,
      referenceSources,
    },
  );
  const name = String(
    record.name ?? record.nom ?? customGroup?.name ?? group.name ?? "",
  );
  return {
    ...customDisciplineRuntimeMetadata(group),
    ...record,
    id: group.id,
    name,
    nom: name,
    selected: Boolean(group.selected),
    competitionFormat: group.competitionFormat ?? null,
    customDisciplineGroupId:
      customGroup?.id ?? group.customDisciplineGroupId ?? null,
    groupTypeId: customGroup?.groupTypeId ?? groupType?.id ?? null,
    groupTypeName: groupType?.name ?? null,
    groupTypeCode: groupType?.code ?? null,
    disciplineId: groupType?.disciplineId ?? groupType?.discipline?.id ?? null,
    categoryDisciplineId: groupType?.discipline?.categoryDisciplineId ?? null,
    disciplineName: groupType?.discipline?.name ?? null,
    disciplineCode: groupType?.discipline?.code ?? null,
  };
}

function buildCustomAthleteRecord(
  athlete: NonNullable<CustomDisciplineGroupLike["athletes"]>[number],
  group: GroupWithCustomDisciplineRuntime,
  referenceSources: Record<string, readonly unknown[]>,
) {
  const groupType = getCustomDisciplineGroupType(group);
  return athleteRecordFromValues(
    getScopeFields(groupType, "ATHLETE"),
    (athlete.values ?? []) as never,
    {
      id: athlete.id,
      groupId: group.id,
      customGroupId: athlete.groupId ?? group.customDisciplineGroup?.id ?? null,
      order: athlete.order,
      referenceSources,
    },
  );
}

/** Shared by local and remote web runtimes; EAV keys and references stay identical. */
export function buildCustomDisciplineLayoutData(input: {
  meet: {
    groups: GroupWithAll[];
    currentCustomAthleteId?: string | null;
  } | null;
  currentGroup: GroupWithAll | null;
  groupTypes: readonly { id: string }[];
  referenceSources: Record<string, readonly unknown[]>;
  categoryReferences: {
    organisations?: import("../layout/types.js").OrganisationData[];
    disciplineAgeCategories?: readonly unknown[];
    disciplineWeightCategories?: readonly unknown[];
  };
}): Partial<InjectedData> | null {
  const { meet, currentGroup, groupTypes, categoryReferences } = input;
  if (!currentGroup || !isCustomDisciplineRuntimeGroup(currentGroup))
    return null;
  const runtimeCurrentGroup = hydrateCustomDisciplineGroupSchema(
    currentGroup,
    groupTypes,
  );
  const currentTypeId = getCustomDisciplineGroupTypeId(runtimeCurrentGroup);
  const related = (meet?.groups ?? [])
    .filter(isCustomDisciplineRuntimeGroup)
    .map((group) => hydrateCustomDisciplineGroupSchema(group, groupTypes))
    .filter(
      (group) =>
        !currentTypeId ||
        getCustomDisciplineGroupTypeId(group) === currentTypeId,
    );
  const groups = related.some((g) => g.id === runtimeCurrentGroup.id)
    ? related
    : [runtimeCurrentGroup, ...related];
  const sourcesWithoutGroups = withCustomDisciplineCategoryReferences({
    ...input.referenceSources,
    ...categoryReferences,
    groups: [],
  });
  const referenceSources = {
    ...sourcesWithoutGroups,
    groups: groups.map((g) => buildCustomGroupRecord(g, sourcesWithoutGroups)),
  };
  const runtimeGroups = groups.map((g) =>
    buildCustomGroupRecord(g, referenceSources),
  );
  const athletes = groups.flatMap((group) =>
    [...(group.customDisciplineGroup?.athletes ?? [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((a) => buildCustomAthleteRecord(a, group, referenceSources)),
  );
  const currentAthlete =
    athletes.find(
      (a) =>
        String(a.id) === meet?.currentCustomAthleteId &&
        a.groupId === runtimeCurrentGroup.id,
    ) ?? null;
  return {
    ...categoryReferences,
    ageCategories: sourcesWithoutGroups.ageCategories,
    weightCategories: sourcesWithoutGroups.weightCategories,
    currentGroup: buildCustomGroupRecord(runtimeCurrentGroup, referenceSources),
    groups: runtimeGroups,
    athletes,
    currentAthlete,
    athlete: currentAthlete,
  } as unknown as Partial<InjectedData>;
}
